import React, { useState, useEffect } from 'react';
import { axiosInstance } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Clock, Phone, Target, Users, Check, X, AlertCircle, Building2, Calendar, RefreshCw } from 'lucide-react';

export default function PulseEntryDrilldownModal({ entryId, open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open && entryId) {
      fetchEntry();
    }
  }, [open, entryId]);

  const fetchEntry = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/pulse-entries/${entryId}`);
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load pulse entry details');
      console.error('Pulse entry fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <Clock className="w-6 h-6 text-primary" />
            <div>
              <DialogTitle className="text-xl">
                {data?.pulse_name || 'Pulse Entry'} - {data?.source_name || 'Loading...'}
              </DialogTitle>
              <p className="text-sm text-muted-foreground">
                {data?.gym_name} • {data?.entry_date}
              </p>
            </div>
          </div>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center h-48">
            <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : data ? (
          <div className="space-y-4 mt-4">
            {/* Entry Info */}
            <div className="grid grid-cols-2 gap-4">
              <Card className="bg-muted/30">
                <CardContent className="p-3">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                    <Building2 className="w-4 h-4" />
                    Gym
                  </div>
                  <p className="font-semibold">{data.gym_name}</p>
                </CardContent>
              </Card>
              <Card className="bg-muted/30">
                <CardContent className="p-3">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                    <Calendar className="w-4 h-4" />
                    Date
                  </div>
                  <p className="font-semibold">{data.entry_date}</p>
                </CardContent>
              </Card>
              <Card className="bg-muted/30">
                <CardContent className="p-3">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                    <Users className="w-4 h-4" />
                    Team Leader
                  </div>
                  <p className="font-semibold">{data.tl_name || '-'}</p>
                </CardContent>
              </Card>
              <Card className="bg-muted/30">
                <CardContent className="p-3">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                    <Users className="w-4 h-4" />
                    Manager
                  </div>
                  <p className="font-semibold">{data.manager_name || '-'}</p>
                </CardContent>
              </Card>
            </div>

            {/* Calling Metrics */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Phone className="w-5 h-5" />
                  Calling Metrics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-4 text-center mb-3">
                  <div className="p-3 rounded-lg bg-muted/30">
                    <p className="text-2xl font-bold">{data.dialed || 0}</p>
                    <p className="text-xs text-muted-foreground">Dialed</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/30">
                    <p className="text-2xl font-bold">{data.connected || 0}</p>
                    <p className="text-xs text-muted-foreground">Connected</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/30">
                    <p className="text-2xl font-bold" style={{
                      color: (data.connect_pct || 0) >= 70 ? '#10b981' :
                             (data.connect_pct || 0) >= 60 ? '#f59e0b' : '#ef4444'
                    }}>
                      {data.connect_pct || 0}%
                    </p>
                    <p className="text-xs text-muted-foreground">Connect Rate</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/30">
                    <p className="text-2xl font-bold">{data.fd_availability ?? '-'}</p>
                    <p className="text-xs text-muted-foreground">FD Available</p>
                  </div>
                </div>
                <div className="pt-2 border-t border-gray-200">
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-muted-foreground">Company IVR Calls:</span>
                    <span className="font-semibold text-green-600">{data.dialed_company_ivr || 0}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">FD Phone Calls:</span>
                    <span className="font-semibold text-amber-600">{data.dialed_fd_phone || 0}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Visit & Closure Metrics */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Target className="w-5 h-5" />
                  Visits & Closures
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-4 text-center">
                  <div className="p-3 rounded-lg bg-muted/30">
                    <p className="text-2xl font-bold">{data.visit_scheduled || 0}</p>
                    <p className="text-xs text-muted-foreground">Visits Scheduled</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/30">
                    <p className="text-2xl font-bold">{data.visit_done || 0}</p>
                    <p className="text-xs text-muted-foreground">Visits Done</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/30">
                    <p className="text-2xl font-bold">{data.closure_expected || 0}</p>
                    <p className="text-xs text-muted-foreground">Closures Expected</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/30">
                    <p className="text-2xl font-bold text-green-500">{data.total_closure || 0}</p>
                    <p className="text-xs text-muted-foreground">Total Closures</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Closure Breakdown (if available) */}
            {(data.closure_walkin > 0 || data.closure_referral > 0 || data.closure_digital > 0) && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    Closure Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 text-center text-sm">
                    <div className="p-2 rounded bg-muted/30">
                      <p className="font-bold">{data.closure_walkin || 0}</p>
                      <p className="text-xs text-muted-foreground">Walk-in</p>
                    </div>
                    <div className="p-2 rounded bg-muted/30">
                      <p className="font-bold">{data.closure_referral || 0}</p>
                      <p className="text-xs text-muted-foreground">Referral</p>
                    </div>
                    <div className="p-2 rounded bg-muted/30">
                      <p className="font-bold">{data.closure_digital || 0}</p>
                      <p className="text-xs text-muted-foreground">Digital</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Verification Status */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  Verification Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-8">
                  <div className="flex items-center gap-2">
                    {data.tl_verified ? (
                      <Check className="w-5 h-5 text-green-500" />
                    ) : (
                      <X className="w-5 h-5 text-red-500" />
                    )}
                    <span className="text-sm">TL Verified</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {data.mgr_verified ? (
                      <Check className="w-5 h-5 text-green-500" />
                    ) : (
                      <X className="w-5 h-5 text-amber-500" />
                    )}
                    <span className="text-sm">Manager Verified</span>
                  </div>
                  {data.flag && (
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{data.flag}</span>
                      <span className="text-sm text-muted-foreground">Flag</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Remarks (if any) */}
            {data.remarks && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Remarks</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">{data.remarks}</p>
                </CardContent>
              </Card>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            Entry not found
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
